import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { FormattedDate } from 'react-intl';
import { ThemeProvider, withTheme } from 'styled-components';

import moment from 'moment-timezone';
import { getDateFromMoment } from '../../../../utils/time';
import { DayPicker, Weekday, NavIcon, MonthTitle, Day, MonthTitleWrapper } from './datePicker.styles';
import { ICON_ARROW_LEFT, ICON_ARROW_RIGHT } from '../../../icon';
import { matchBreakpoint } from '../../../../../theme/getters';
import { Breakpoints } from '../../../../../theme/media';
import { isFirstDayOfMonth, isLastDayOfMonth } from '../../../../utils/date';

const AVAILABLE_DAYS_COUNT = 90;
const TABLET_MONTHS_COUNT = 2;

const getDateWhenExists = date => (date ? moment(date).toDate() : undefined);

export class DatePickerBase extends PureComponent {
  static propTypes = {
    className: PropTypes.string,
    activeDate: PropTypes.object,
    onDateSelected: PropTypes.func.isRequired,
  };

  static defaultProps = {
    activeDate: {},
  };

  state = {
    from: null,
  };

  static getDerivedStateFromProps(props) {
    if (!props.activeDate.from) {
      return {
        from: null,
      };
    }

    return null;
  }

  getIsTablet = () => matchBreakpoint(Breakpoints.TABLET)(this.props);

  getNumberOfMonths = () => {
    if (this.getIsTablet()) {
      return TABLET_MONTHS_COUNT;
    }

    const { before, after } = this.disabledDays;
    return moment(after).diff(moment(before), 'months') + 2;
  };

  disabledDays = {
    before: moment().toDate(),
    after: moment()
      .add(AVAILABLE_DAYS_COUNT, 'days')
      .toDate(),
  };

  formatMonthTitle = date => <FormattedDate value={date} year="numeric" month="long" />;

  isLastDayOfMonthAndStartIsNotSelected = date => isLastDayOfMonth(date) && !this.state.from;

  handleDayClick = (day, modifiers = {}) => {
    if (modifiers.disabled) {
      return;
    }

    const { from } = this.state;

    if (from) {
      const isDayBeforeFrom = moment(day).isBefore(from);
      this.props.onDateSelected(isDayBeforeFrom ? { from: day, to: from } : { from, to: day });
      this.setState({ from: null });
      return;
    }

    this.setState({ from: day });
    this.props.onDateSelected({ from: day, to: day });
  };

  renderNavbar = ({ month, onPreviousClick, onNextClick }) => {
    if (!this.getIsTablet()) {
      return null;
    }

    const isSameMonth = moment().isSame(month, 'month');
    const isAfterDisabledDay = moment(month).isSame(this.disabledDays.after, 'month');

    return (
      <>
        <ThemeProvider theme={{ left: true }}>
          <NavIcon stroked name={ICON_ARROW_LEFT} onClick={() => onPreviousClick()} inactive={isSameMonth} />
        </ThemeProvider>
        <NavIcon stroked name={ICON_ARROW_RIGHT} onClick={() => onNextClick()} inactive={isAfterDisabledDay} />
      </>
    );
  };

  renderDay = (day, modifiers) => {
    const charCount = `${day.getDate()}`.length;
    return (
      <ThemeProvider theme={{ ...modifiers, hasSingleCharDay: charCount === 1 }}>
        <Day>{day.getDate()}</Day>
      </ThemeProvider>
    );
  };

  renderWeekday = ({ weekday, className }) => {
    const value = getDateFromMoment(moment().day(weekday));
    return (
      <Weekday className={className}>
        <FormattedDate value={value} weekday="short" />
      </Weekday>
    );
  };

  renderCaption = ({ date, className }) => {
    const isCurrentMonth = moment(date).isSame(moment(), 'month');
    const monthTitle = this.formatMonthTitle(date);

    return (
      <MonthTitleWrapper isCurrentMonth={isCurrentMonth} className={className}>
        <MonthTitle>{monthTitle}</MonthTitle>
      </MonthTitleWrapper>
    );
  };

  render() {
    const { from: activeFrom, to: activeTo } = this.props.activeDate;
    const { from } = this.state;
    const start = from || getDateWhenExists(activeFrom);
    const end = from || getDateWhenExists(activeTo);
    const initialMonth = matchBreakpoint(Breakpoints.TABLET)(this.props) ? start : undefined;

    const modifiers = {
      start,
      end,
      firstDayOfWeek: { daysOfWeek: [1] },
      lastDayOfWeek: { daysOfWeek: [0] },
      firstDayOfMonth: isFirstDayOfMonth,
      lastDayOfMonth: this.isLastDayOfMonthAndStartIsNotSelected,
    };

    const { before, after } = this.disabledDays;

    return (
      <DayPicker
        className={this.props.className}
        onDayClick={this.handleDayClick}
        modifiers={modifiers}
        selectedDays={{ from: start, to: end }}
        firstDayOfWeek={1}
        weekdayElement={this.renderWeekday}
        navbarElement={this.renderNavbar}
        captionElement={this.renderCaption}
        numberOfMonths={this.getNumberOfMonths()}
        renderDay={this.renderDay}
        disabledDays={[{ before }, { after }]}
        initialMonth={initialMonth}
      />
    );
  }
}

export const DatePicker = withTheme(DatePickerBase);
